import type { ReactElement } from 'react';
import Script from 'next/script';
import type { CustomHtmlNode } from 'data-layer';
import { CUSTOM_JAVASCRIPT_TYPE } from '../../constants';
import type { CustomHtmlProps } from './types';

// Build metadata comment if we have id or internalName
const createHtmlComment = (node: CustomHtmlNode): string => {
  const { id, internalName } = node;
  const metadataParts: string[] = [];

  if (id) {
    metadataParts.push(`Entry ID: ${id}`);
  }

  if (internalName) {
    metadataParts.push(`Internal Name: ${internalName}`);
  }

  if (metadataParts.length > 0) {
    return `<!-- Custom Content: (${metadataParts.join(', ')}) -->`;
  }

  return '';
};

/**
 * CustomHtml component renders HTML content from CMS using dangerouslySetInnerHTML
 *
 * ## Security Model
 *
 * # Why we don't sanitize:
 * - Content originates from Contentful, a trusted CMS with role-based access control
 * - Only authorized team members (marketing, content editors) can create/edit content
 * - We need to allow custom JavaScript, styles, and interactive elements (like <script>, <details>, etc.)
 * - Once you allow <script> tags, client-side sanitization becomes ineffective for security
 * - The security boundary is CMS access control, not client-side sanitization
 *
 * # What we do instead:
 * - Server-side validation in the data-layer transformer checks for:
 *   - Malformed HTML (unclosed tags, invalid structure)
 *   - Unbalanced tags that could break page layout
 *   - Extremely nested tags (potential DoS)
 *   - NULL bytes and invalid characters
 * - Validation errors are logged to GitLab pipeline logs with Contentful metadata
 * - Invalid content is stripped and replaced with empty string to prevent broken layouts
 * - Only valid HTML content reaches this component
 *
 * # Risk acceptance:
 * - If a bad actor gains access to Contentful, they could inject malicious scripts
 * - This risk is mitigated by:
 *   - Contentful's authentication and authorization controls
 *   - Audit logs for all content changes
 *   - Content review workflows before publication
 * - The business value of allowing content creators full HTML/CSS/JS flexibility
 *   outweighs the risk of the unlikely scenario of CMS compromise
 *
 * # Why dangerouslySetInnerHTML:
 * - Simplest way to transfer the content from Contentful to the DOM as is.
 *
 * # Rendering behaviour:
 * - `customCss` → `<style precedence="default">` hoisted into `<head>` by React 19 (prevents FOUC)
 * - `externalStylesheets` → `<link rel="stylesheet" precedence="default">` per URL, loaded in parallel in `<head>`
 * - `customHtml` → `<section>` (with sectionProps) or `<div>`, rendered via dangerouslySetInnerHTML
 * - `customJavaScript` with type `classic` → `next/script strategy="afterInteractive"` (global scope, document.write support)
 * - `customJavaScript` with type `module` (default) → `<script type="module">` (deferred, scoped, no global leaks)
 * - `externalScripts` → `<script type="module" src="...">` per URL
 *
 * # Content debugging:
 * - For empty/invalid content: renders a minimal div with HTML comment containing metadata
 *   - With both id and internalName: `<!-- Custom Content: (Entry ID: xxx, Internal Name: xxx) -->`
 *   - With only id: `<!-- Custom Content: (Entry ID: xxx) -->`
 *   - With only internalName: `<!-- Custom Content: (Internal Name: xxx) -->`
 * - When content is empty without any metadata (no id or internalName), returns null (renders nothing)
 * - Valid content is rendered directly without any debug comments
 * - This helps identify which CMS content is being rendered in the DOM for debugging purposes
 *
 * @param props - Component props
 * @param props.node - CustomHtml node with all CMS fields: `customHtml`, `customCss`, `customJavaScript`,
 *   `customJavaScriptType` (`CUSTOM_JAVASCRIPT_TYPE.MODULE` | `CUSTOM_JAVASCRIPT_TYPE.CLASSIC`),
 *   `externalScripts`, `externalStylesheets`, `id`, and `internalName`
 * @param props.sectionProps - Optional props for the wrapper section element (only applied when customHtml is not empty)
 */
export function CustomHtml(props: CustomHtmlProps): ReactElement | null {
  const { node, sectionProps } = props;
  const { customHtml, customCss, customJavaScript, customJavaScriptType } = node;
  const externalScripts = node.externalScripts ?? [];
  const externalStylesheets = node.externalStylesheets ?? [];

  const isHtmlEmpty = !customHtml || customHtml.trim().length === 0;
  // Ensure CSS ends with a newline so React 19's style hoisting doesn't concatenate
  // adjacent blocks without whitespace when multiple <style precedence> tags are merged.
  const normalizedCss = customCss ? `${customCss.trimEnd()}\n\n` : customCss;
  const hasCss = normalizedCss && normalizedCss.trim().length > 0;
  const hasJs = customJavaScript && customJavaScript.trim().length > 0;
  const hasExternalScripts = externalScripts.length > 0;
  const hasExternalStylesheets = externalStylesheets.length > 0;

  // If all fields are empty, render a debug comment or nothing
  if (isHtmlEmpty && !hasCss && !hasJs && !hasExternalScripts && !hasExternalStylesheets) {
    const comment = createHtmlComment(node);

    if (comment) {
      return <div dangerouslySetInnerHTML={{ __html: comment }} />;
    }

    // No metadata, return null (render nothing)
    return null;
  }

  const htmlContent = (
    <>
      {/* React 19 hoists <style precedence> into <head> during SSR, preventing FOUC (Flash of Unstyled Content) */}
      {hasCss && (
        <style precedence="default" href={`custom-html-css-${node.id ?? node.internalName}`}>
          {normalizedCss}
        </style>
      )}

      {/* External stylesheets loaded in parallel via <link> in <head> */}
      {hasExternalStylesheets &&
        externalStylesheets.map(href => <link key={href} rel="stylesheet" href={href} precedence="default" />)}

      {/* If sectionProps are provided, wrap in a section element */}
      {!isHtmlEmpty && sectionProps && (
        <section {...sectionProps} dangerouslySetInnerHTML={{ __html: customHtml ?? '' }} />
      )}
      {!isHtmlEmpty && !sectionProps && <div dangerouslySetInnerHTML={{ __html: customHtml ?? '' }} />}

      {/* Inject JS as a deferred script to keep it non-blocking.
          module (default): <script type="module"> — deferred, scoped variables, no global leaks.
          classic: next/script afterInteractive — for legacy scripts requiring global scope or document.write. */}
      {hasJs && customJavaScriptType === CUSTOM_JAVASCRIPT_TYPE.CLASSIC ? (
        <Script id={`custom-html-js-${node.id ?? node.internalName}`} strategy="afterInteractive">
          {customJavaScript}
        </Script>
      ) : hasJs ? (
        <script
          type="module"
          id={`custom-html-js-${node.id ?? node.internalName}`}
          dangerouslySetInnerHTML={{ __html: customJavaScript ?? '' }}
        />
      ) : null}

      {/* External scripts loaded as <script type="module" src="..."> */}
      {hasExternalScripts && externalScripts.map(src => <script key={src} type="module" src={src} />)}
    </>
  );

  return htmlContent;
}
